import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: 'SA',
    mobileDigits: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Check for message from URL and state from login
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const message = urlParams.get('message');
    if (message) {
      setInfoMessage(decodeURIComponent(message));
    }

    // Check for passed state from Login
    if (location.state) {
      const state = location.state as { mobileDigits?: string, country?: string };
      if (state.mobileDigits || state.country) {
        setFormData(prev => ({
          ...prev,
          mobileDigits: state.mobileDigits || prev.mobileDigits,
          country: state.country || prev.country
        }));
        // Optional: show a message that we pre-filled the data
        if (!message) {
          // You might want to add a translation key for this
          // setInfoMessage(t('completeRegistrationPrompt')); 
        }
      }
    }
  }, [location.state]);

  const handleChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.password !== formData.confirmPassword) {
      setError(t('registerErrorPasswordMismatch') as string);
      setLoading(false);
      return;
    }

    try {
      const code = formData.country === 'SA' ? '+966' : '+249';
      const digitsOnly = /^[0-9]+$/.test(formData.mobileDigits);
      if (!digitsOnly) {
        setError(t('mobileDigitsOnly') as string);
        setLoading(false);
        return;
      }
      let local = formData.mobileDigits;
      if (local.length === 10 && local.startsWith('0')) {
        local = local.substring(1);
      }
      if (local.length !== 9) {
        setError(t('mobileLengthInvalid', { count: 9 }) as string);
        setLoading(false);
        return;
      }
      const fullMobile = `${code}${local}`;
      const success = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        mobileNumber: fullMobile,
        password: formData.password
      });

      if (success) {
        // Check if there's a pending product request
        const pendingRequest = localStorage.getItem('pendingProductRequest');
        
        if (pendingRequest) {
          // Redirect to product request page to auto-submit
          navigate('/product-request');
        } else {
        navigate('/');
        }
      } else {
        setError(t('registerErrorFailed') as string);
      }
    } catch (err) {
      setError(t('registerErrorGeneric') as string);
    }

    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>{t('register')} - MIC Green Store</title>
        <meta name="description" content="Create your account at MIC Green Store" />
        <meta property="og:title" content={`${t('register')} - MIC Green Store`} />
        <meta property="og:description" content="Create your account at MIC Green Store" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
      </Helmet>

      <Container className="py-5" style={{ marginTop: '120px', direction: isRTL ? 'rtl' : 'ltr' }}>
        <Row className="justify-content-center">
          <Col md={8} lg={7}>
            <Card className="shadow-lg border-0" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <i className="fas fa-user-plus fa-3x text-primary mb-3"></i>
                  </div>
                  <h2 className="fw-bold mb-2" style={{ 
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #059669 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    {t('registerForm')}
                  </h2>
                  <p className="text-muted">{t('createAccountMsg')}</p>
                </div>
                
                {error && <Alert variant="danger">{error}</Alert>}
                {infoMessage && (
                  <Alert variant="info" className="mb-3">
                    <i className="fas fa-info-circle me-2"></i>
                    {infoMessage}
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold">
                          <i className="fas fa-user me-2 text-primary"></i>
                          {t('firstName')}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder={t('firstNamePlaceholder') as string}
                          required
                          className="py-2"
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold">
                          <i className="fas fa-user me-2 text-primary"></i>
                          {t('lastName')}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder={t('lastNamePlaceholder') as string}
                          required
                          className="py-2"
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-mobile-alt me-2 text-primary"></i>
                      {t('mobileNumber')}
                    </Form.Label>
                    <InputGroup dir="ltr">
                      <Form.Select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="py-2 bg-light"
                        style={{ maxWidth: '140px', fontWeight: 'bold' }}
                      >
                        <option value="SA">🇸🇦 +966</option>
                        <option value="SD">🇸🇩 +249</option>
                      </Form.Select>
                      <Form.Control
                        type="tel"
                        name="mobileDigits"
                        value={formData.mobileDigits}
                        onChange={handleChange}
                        placeholder={t('enterMobileDigits') as string}
                        required
                        className="py-2"
                        inputMode="numeric"
                      />
                    </InputGroup>
                  </Form.Group>

                                 <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-envelope me-2 text-primary"></i>
                      {t('email')}
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('emailPlaceholder') as string}
                      className="py-2"
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-lock me-2 text-primary"></i>
                      {t('password')}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={t('passwordRegisterPlaceholder') as string}
                      required
                      minLength={6}
                      className="py-2"
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="fas fa-lock me-2 text-primary"></i>
                      {t('confirmPassword')}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder={t('confirmPasswordPlaceholder') as string}
                      required
                      minLength={6}
                      className="py-2"
                      dir={isRTL ? 'rtl' : 'ltr'}
                    />
                  </Form.Group>
                  
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className="w-100 mb-3 py-2 fw-semibold"
                    disabled={loading}
                    style={{
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      border: 'none',
                      fontSize: '1.1rem'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('creatingAccount')}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-user-plus me-2"></i>
                        {t('submit')}
                      </>
                    )}
                  </Button>
                </Form>
                
                <div className="text-center mt-4">
                  <p className="mb-0">
                    {t('alreadyHaveAccount')}{' '}
                    <Link 
                      to="/login" 
                      style={{ 
                        color: '#059669', 
                        textDecoration: 'none',
                        fontWeight: '600'
                      }}
                    >
                      {t('signIn')}
                    </Link>
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Register;
